import { type Industry } from '@/domain/industry/types';
import { type Industries, type ProductSimple } from '@/domain/products/types';

import { type Filter, FilterGroup, type Func, type ShowType } from '../types';

function checkIndustry(value: Industry, industry: Industries): boolean {
  switch (industry) {
    case 'Specific':
      return value.isSpecific;
    case 'Generic':
      return !value.isSpecific;
    default:
      return true;
  }
}

export const filterFunctions: {
  [key in FilterGroup]: Func;
} = {
  [FilterGroup.USER_PERSONA]: (p: ProductSimple, id: number) => p.userPersonas.some((u) => u.idUserPersona === id),

  [FilterGroup.INDUSTRY]: (p: ProductSimple, id: number, industry: Industries) =>
    p.industries.some((i) => i.idIndustry === id && checkIndustry(i, industry)),

  [FilterGroup.INDUSTRY_AREA]: (p: ProductSimple, id: number, industry: Industries) =>
    p.industries.some((i) => i.industryArea?.idIndustryArea === id && checkIndustry(i, industry)),

  [FilterGroup.PRODUCT_LINE]: (p: ProductSimple, id: number) => p.group.productLine?.idProductLine === id,

  [FilterGroup.PRODUCT_GROUP]: (p: ProductSimple, id: number) => p.group.idProductGroup === id,

  [FilterGroup.BENEFIT_GROUP]: (p: ProductSimple, id: number) =>
    p.benefitGroups.some((i) => i.idBenefitGroup === id),

  [FilterGroup.PLANT_LIFECYCLE_GROUP]: (p: ProductSimple, id: number) =>
    p.lifeCycles.some((l) => l.lifeCycleGroup?.idLifeCycleGroup === id),

  [FilterGroup.PLANT_LIFECYCLE]: (p: ProductSimple, id: number) => p.lifeCycles.some((i) => i.idLifeCycle === id),

  [FilterGroup.BUSINESS_MODEL]: (p: ProductSimple, id: number) =>
    p.group?.productLine?.businessModel?.idBusinessModel === id,

  [FilterGroup.PLATFORM]: (p: ProductSimple, id: number) => p.platform?.idPlatform === id,

  [FilterGroup.REGION]: (p: ProductSimple, id: number) => p.countries.some((i) => i.region?.idCountryRegion === id),

  [FilterGroup.COUNTRY]: (p: ProductSimple, id: number) => p.countries.some((i) => i.idCountry === id),

  [FilterGroup.PORTFOLIO]: (p: ProductSimple, id: number) => p.portfolios.some((i) => i.idProductPortfolio === id),

  [FilterGroup.PRODUCT_UNIT]: (p: ProductSimple, id: number) => p.productUnit?.unitId === id,
};

export /**
 * @param {Product[]} product - array of products
 * @param {Filter[]} filters - array of filters
 * @param {Group} group - filter group
 * @returns {boolean} whether product matches provided filters
 */
const filterProduct = (product: ProductSimple, filters: Filter[], group: FilterGroup, industry?: Industries) => {
  const filtered = filters.filter((filter) => filter.group === group);
  if (!filtered.length) return true;
  return filtered.some((filter) => filterFunctions[group](product, filter.id, industry));
};

export const filterShowType = (product: ProductSimple, showType: ShowType[]): boolean => {
  if (!showType.length) return true;

  const isSolution = showType.includes('Show Solutions') && product.idType === 0;
  const isComponent = showType.includes('Show Components') && product.idType === 1;

  return isSolution || isComponent;
};
